import React, { useEffect } from 'react';
import { S } from './Users.style';
import { Input } from '../../components/Input';
import { useNavigate, useParams } from 'react-router-dom';
import { MultiSelect, Option } from '../../components/MultiSelect';
import {
  SortControls,
  useGetMyProjects,
} from '../../traits/api/projects/useGetMyProjects';
import { Checkbox } from '../../components/Checkbox';
import { useAuth } from '../../traits/hooks/useAuth';
import {
  CustomerUserDetail,
  useGetUser,
} from '../../traits/api/users/useGetUser';
import { useUpdateUser } from '../../traits/api/users/useUpdateUser';
import { useReplaceUserProjects } from '../../traits/api/users/useReplaceUserProjects';

export const EditUser = () => {
  const { userId } = useParams();
  const route = useNavigate();
  const { isCustomerAdmin } = useAuth();
  const { data: user, isLoading } = useGetUser(userId);
  const { data: projects } = useGetMyProjects(
    0,
    SortControls.ProjectNameAsc,
    undefined,
    1000
  );
  const updateUser = useUpdateUser();
  const replaceProjects = useReplaceUserProjects();

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [hidden, setHidden] = React.useState(false);
  const [hasAllProjectAccess, setHasAllProjectAccess] = React.useState(true);
  const [selectedProjects, setSelectedProjects] = React.useState<Option[]>([]);

  useEffect(() => {
    if (!user) return;
    const detail = user as CustomerUserDetail;
    setFirstName(detail.firstName);
    setLastName(detail.lastName);
    setHidden(detail.hidden);
    setHasAllProjectAccess(detail.hasAllProjectAccess);
    const projectIds =
      detail.projectCustomerUser?.map((p) => p.projectId) ?? [];
    if (projects) {
      setSelectedProjects(
        projects
          .filter((p) => projectIds.includes(p.projectId))
          .map((p) => ({ key: p.projectId, label: p.name }))
      );
    }
  }, [user, projects]);

  if (!isCustomerAdmin) {
    return <S.H6>Du har inte behörighet att se denna sida</S.H6>;
  }

  if (isLoading || !user) {
    return <div>Loading...</div>;
  }

  const save = async () => {
    if (!userId) return;
    await updateUser.mutateAsync({
      customerUserId: userId,
      data: {
        firstName,
        lastName,
        hidden,
        hasAllProjectAccess,
      },
    });
    if (!hasAllProjectAccess) {
      await replaceProjects.mutateAsync({
        customerUserId: userId,
        data: { projectIds: selectedProjects.map((p) => p.key) },
      });
    }
    route('/users');
  };

  return (
    <>
      <S.HeaderContainer>
        <S.H3>Redigera användare</S.H3>
      </S.HeaderContainer>
      <S.Form>
        <S.FormRow>
          <Input
            value={firstName}
            onChange={(ev) => setFirstName(ev.target.value)}
            type="text"
            placeholder="Förnamn"
          />
          <Input
            value={lastName}
            onChange={(ev) => setLastName(ev.target.value)}
            type="text"
            placeholder="Efternamn"
          />
        </S.FormRow>
        <S.FormRow>
          <Checkbox
            checked={hidden}
            onChange={() => setHidden(!hidden)}
            label="Dölj personen"
          />
        </S.FormRow>
        <S.FormRow>
          <Checkbox
            checked={!hasAllProjectAccess}
            onChange={() => {
              const next = !hasAllProjectAccess;
              setHasAllProjectAccess(next);
              if (next) {
                setSelectedProjects([]);
              }
            }}
            label="Begränsa till specifika projekt"
          />
        </S.FormRow>
        {!hasAllProjectAccess && (
          <S.FormRow>
            <MultiSelect
              label="Välj vilka projekt användaren ska ha tillgång till"
              optionLabel="Välj projekt"
              searchPlaceholder="Sök projekt..."
              selectAllLabel="Alla projekt"
              chosenOptions={selectedProjects}
              setChosenOptions={setSelectedProjects}
              options={
                projects
                  ? projects.map((project) => ({
                      key: project.projectId,
                      label: project.name,
                    }))
                  : []
              }
            />
          </S.FormRow>
        )}
        <S.FormRow>
          <S.Button
            $outlined
            onClick={(ev) => {
              ev.preventDefault();
              route('/users');
            }}
          >
            Avbryt
          </S.Button>
          <S.Button
            onClick={(ev) => {
              ev.preventDefault();
              save();
            }}
          >
            Spara
          </S.Button>
        </S.FormRow>
      </S.Form>
    </>
  );
};
